import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PageShell from "../components/PageShell";
import { useAuth } from "../context/AuthContext";
import "./EmployeeDirectory.css";

/**
 * EmployeeDirectory (tenant-scoped)
 * Step 2: Adds reporting relationship views without changing existing directory flow.
 * - Everyone still sees the tenant-scoped directory (GET /api/employees)
 * - Employees additionally see "My manager"
 * - Managers additionally see "My team" (direct reports)
 * - Admins get a shortcut to Admin -> People for assignments
 */

export default function EmployeeDirectory() {
  const { user } = useAuth();

  // Step 3: Managers can invite employees (creates a copyable invite link)
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteStatus, setInviteStatus] = useState("");
  const [notice, setNotice] = React.useState("");

  // Step 3.1: Manager CSV import
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvError, setCsvError] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [inviteError, setInviteError] = useState("");

  const [rows, setRows] = useState([]);
  const [myManager, setMyManager] = useState(null);
  const [myReports, setMyReports] = useState([]);
  const [myInvites, setMyInvites] = useState([]);

  const [loading, setLoading] = useState(true);
  const [relLoading, setRelLoading] = useState(false);

  const [error, setError] = useState("");
  const importEmployeesCsv = async () => {
    try {
      setCsvBusy(true);
      setCsvError("");
      setCsvResult(null);
      if (!csvFile) {
        setCsvError("Please choose a CSV file.");
        return;
      }
      const form = new FormData();
      form.append("file", csvFile);

      const res = await axios.post("/api/manager/import/employees", form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(res.data);
      setCsvFile(null);

      // refresh team + directory
      await fetchEmployees();
      await fetchMyReports();
      await fetchMyInvites();
      await fetchRelationships();
    } catch (e) {
      const msg = e?.response?.data?.error || "Failed to import CSV.";
      setCsvError(msg);
    } finally {
      setCsvBusy(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/directory", { withCredentials: true });
      setRows(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (e) {
      setError("Failed to load directory");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationships = async () => {
    if (!user?.role) return;
    try {
      setRelLoading(true);

      if (user.role === "user") {
        const res = await axios.get("/api/users/me/manager", {
          withCredentials: true,
        });
        setMyManager(res.data || null);
        setMyReports([]);
      } else if (user.role === "team_manager") {
        const res = await axios.get("/api/users/me/reports", {
          withCredentials: true,
        });
        setMyReports(Array.isArray(res.data) ? res.data : []);
        setMyManager(null);
      } else {
        // company_admin
        setMyManager(null);
        setMyReports([]);
      }
    } catch (e) {
      // Don't block directory if relationship calls fail
      setMyManager(null);
      setMyReports([]);
    } finally {
      setRelLoading(false);
    }
  };

  const fetchMyInvites = async () => {
    if (user?.role !== "team_manager") {
      setMyInvites([]);
      return;
    }
    try {
      const res = await axios.get("/api/manager/invites?status=pending", {
        withCredentials: true,
      });
      setMyInvites(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setMyInvites([]);
    }
  };

  const fetchMyReports = async () => {
    // Backward-compatible helper: used by CSV/invite flows to refresh "My team"
    if (user?.role !== "team_manager") {
      setMyReports([]);
      return;
    }
    try {
      const res = await axios.get("/api/users/me/reports", {
        withCredentials: true,
      });
      setMyReports(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setMyReports([]);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRelationships();
    fetchMyInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const title = useMemo(() => {
    if (user?.role === "team_manager") return "Team directory";
    if (user?.role === "company_admin") return "Company directory";
    return "Employee directory";
  }, [user?.role]);

  const createInvite = async () => {
    try {
      setInviteBusy(true);
      setInviteError("");
      setInviteLink("");
      setInviteStatus("");
      const res = await axios.post(
        "/api/invites",
        { email: inviteEmail.trim(), role: "user" },
        { withCredentials: true },
      );
      setInviteLink(res.data?.accept_url || "");
      setInviteStatus(
        res.data?.email_sent
          ? "Invite emailed to the employee."
          : res.data?.email_error
            ? "Email not sent: " + res.data.email_error
            : "Email not sent (email not configured).",
      );
      setInviteEmail("");
      await fetchMyInvites();
      await fetchRelationships();
    } catch (e) {
      setInviteError(e?.response?.data?.error || "Failed to create invite.");
    } finally {
      setInviteBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  return (
    <PageShell
      title={title}
      subtitle="This directory is scoped to your company. Reporting relationships are now enabled (manager ↔ employee)."
    >
      {error ? (
        <div className="pill edAlert edAlert--error">{error}</div>
      ) : null}
      {notice ? (
        <div className="pill edAlert edAlert--success">{notice}</div>
      ) : null}

      {user?.role === "team_manager" ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Invite an employee
            </div>
            <div className="edReadOnly" style={{ marginBottom: 10 }}>
              Creates a secure invite link. Company policies may restrict
              domains and daily invite volume.
            </div>

            {inviteError ? (
              <div className="pill edAlert edAlert--error">{inviteError}</div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "end",
              }}
            >
              <div style={{ minWidth: 280 }}>
                <label className="label">Employee email</label>
                <input
                  className="input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="employee@company.com"
                  disabled={inviteBusy}
                />
              </div>

              <button
                className="btn primary"
                onClick={createInvite}
                disabled={inviteBusy || !inviteEmail.trim()}
              >
                {inviteBusy ? "Creating…" : "Create invite"}
              </button>
            </div>

            {inviteLink ? (
              <div style={{ marginTop: 12 }}>
                <div className="edReadOnly" style={{ marginBottom: 6 }}>
                  Invite link
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <code
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "var(--card-soft)",
                      flex: "1 1 520px",
                    }}
                  >
                    {inviteLink}
                  </code>
                  <button className="btn" onClick={() => copy(inviteLink)}>
                    Copy
                  </button>
                </div>
                {inviteStatus ? (
                  <div className="edReadOnly" style={{ marginTop: 6 }}>
                    {inviteStatus}
                  </div>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginTop: 10,
                  }}
                ></div>
              </div>
            ) : null}

            {user?.role === "team_manager" ? (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card__body">
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>
                    Import employees via CSV
                  </div>
                  <div className="edReadOnly" style={{ marginBottom: 10 }}>
                    CSV columns: <code>email</code> (required). Employees will
                    be added to your team.
                  </div>

                  {csvError ? (
                    <div className="pill edAlert edAlert--error">
                      {csvError}
                    </div>
                  ) : null}

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                      disabled={csvBusy}
                    />
                    <button
                      className="btn primary"
                      onClick={importEmployeesCsv}
                      disabled={csvBusy || !csvFile}
                    >
                      {csvBusy ? "Uploading…" : "Upload CSV"}
                    </button>
                  </div>

                  {csvResult?.created?.length ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        Created invite links
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table className="edTable" style={{ width: "100%" }}>
                          <thead>
                            <tr>
                              <th>Email</th>
                              <th>Invite link</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvResult.created.map((inv) => (
                              <tr key={inv.invite_id}>
                                <td>{inv.email}</td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 10,
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                    }}
                                  >
                                    <a
                                      href={inv.accept_url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Open
                                    </a>
                                    <button
                                      className="btn"
                                      onClick={() => copy(inv.accept_url)}
                                    >
                                      Copy
                                    </button>
                                  </div>
                                </td>
                                <td className="edReadOnly">
                                  {inv.email_sent
                                    ? "Email sent"
                                    : inv.email_error
                                      ? `Email not sent: ${inv.email_error}`
                                      : "Email not sent"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Pending invites (manager) */}
      {user?.role === "team_manager" ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontWeight: 700 }}>Pending invites</div>
              <div className="edReadOnly">{myInvites.length} pending</div>
            </div>

            {myInvites.length === 0 ? (
              <div className="edEmpty" style={{ marginTop: 12 }}>
                No pending invites.
              </div>
            ) : (
              <div className="edTableWrap" style={{ marginTop: 12 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Expires</th>
                      <th style={{ width: 220 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myInvites.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.email}</td>
                        <td className="edReadOnly">{inv.role}</td>
                        <td className="edReadOnly">
                          {inv.expires_at
                            ? new Date(inv.expires_at).toLocaleString()
                            : "—"}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              className="btn"
                              onClick={async () => {
                                try {
                                  const res = await axios.post(
                                    `/api/invites/${inv.id}/resend`,
                                    {},
                                    { withCredentials: true },
                                  );
                                  const url = res.data?.accept_url;
                                  if (url) await copy(url);
                                  await fetchMyInvites();
                                  setNotice(
                                    url
                                      ? "New invite link copied."
                                      : "Invite regenerated.",
                                  );
                                } catch (e) {
                                  alert(
                                    e?.response?.data?.error || "Resend failed",
                                  );
                                }
                              }}
                            >
                              Resend / new link
                            </button>
                            <button
                              className="btn"
                              onClick={async () => {
                                if (!window.confirm("Revoke this invite?"))
                                  return;
                                try {
                                  await axios.post(
                                    `/api/invites/${inv.id}/revoke`,
                                    {},
                                    { withCredentials: true },
                                  );
                                  await fetchMyInvites();
                                  setNotice("Invite revoked.");
                                } catch (e) {
                                  alert(
                                    e?.response?.data?.error || "Revoke failed",
                                  );
                                }
                              }}
                            >
                              Revoke
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Relationship cards */}
      {user?.role === "user" ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>My manager</div>
            {relLoading ? (
              <div className="edReadOnly">Loading…</div>
            ) : myManager ? (
              <div>
                <div>{myManager.name || "—"}</div>
                <div className="edReadOnly">{myManager.email || ""}</div>
              </div>
            ) : (
              <div className="edReadOnly">No manager assigned yet.</div>
            )}
          </div>
        </div>
      ) : null}

      {user?.role === "team_manager" ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontWeight: 700 }}>My team</div>
              <div className="edReadOnly">
                {relLoading
                  ? "Loading…"
                  : `${myReports.length} direct report${myReports.length === 1 ? "" : "s"}`}
              </div>
            </div>

            {!relLoading && myReports.length === 0 ? (
              <div className="edEmpty" style={{ marginTop: 12 }}>
                No direct reports assigned yet.
              </div>
            ) : null}

            {!relLoading && myReports.length > 0 ? (
              <div className="edTableWrap" style={{ marginTop: 12 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myReports.map((r) => (
                      <tr key={r.id}>
                        <td>{r.name || "—"}</td>
                        <td>{r.email || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {user?.role === "company_admin" ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__body">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Admin tip</div>
            <div className="edReadOnly">
              Assign employees to managers in{" "}
              <a href="/admin/people">Admin → People</a>.
            </div>
          </div>
        </div>
      ) : null}

      {/* Directory (existing flow) */}
      <div className="card">
        <div className="card__body">
          {loading ? (
            <div className="edEmpty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="edEmpty">No users found.</div>
          ) : (
            <div className="edTableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name || "—"}</td>
                      <td>{r.email || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
